import { Box, Button, Typography } from '@mui/material'
import { NonNumericOrderDto } from '@planning/app/api'
import { NonNumericOrderWithPickUpAmount } from '@planning/stores/truckAppointment/TruckAppointmentDetailsViewStore'
import { useTranslate } from '@tolgee/react'
import { IconButton, MinusIcon, PlusIcon, useMinimalsTheme } from '@tom-ui/ui'
import { observer } from 'mobx-react-lite'
import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { NoContainersAlert } from './NoContainersAlert'

interface IProps {
  order: NonNumericOrderDto | NonNumericOrderWithPickUpAmount
  isForAppointment?: boolean
  isEditing?: boolean
  onClickAdd?: (order: NonNumericOrderWithPickUpAmount) => Promise<void> | void
}

export const NNROrderHandlingBoxFooter: FC<IProps> = observer(
  ({ order: nnrOrder, isForAppointment, isEditing, onClickAdd }) => {
    const { t } = useTranslate()
    const theme = useMinimalsTheme()

    const [count, setCount] = useState(1)
    const [doesNotEnoughContainersAvailable, setDoesNotEnoughContainersAvailable] =
      useState<boolean>(false)

    const disableActions = useMemo(
      () => !isForAppointment && doesNotEnoughContainersAvailable,
      [isForAppointment, doesNotEnoughContainersAvailable],
    )

    const remainingOrdersCount = nnrOrder.availableForPickUpOrdersCount

    const setWarning = useCallback(() => {
      const containersAvailable = nnrOrder.suitableContainersCount ?? 0
      if (containersAvailable < count) setDoesNotEnoughContainersAvailable(true)
    }, [count, nnrOrder.suitableContainersCount])

    useEffect(() => {
      const pickUpAmount = (nnrOrder as NonNumericOrderWithPickUpAmount).pickUpAmount
      if (pickUpAmount) {
        setCount(pickUpAmount)
      }
    }, [nnrOrder])

    useEffect(() => {
      setWarning()
    }, [setWarning, nnrOrder.suitableContainersCount])

    const handleClickAdd = async () => {
      setWarning()

      if ((nnrOrder.suitableContainersCount ?? 0) >= count || isForAppointment)
        onClickAdd?.({ ...nnrOrder, pickUpAmount: count })
    }

    return (
      <Box>
        {isEditing && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: theme.customSpacing.m,
            }}
          >
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <IconButton
                  onClick={() => {
                    setCount(count - 1)
                    setDoesNotEnoughContainersAvailable(false)
                  }}
                  disabled={count <= 1}
                >
                  <MinusIcon />
                </IconButton>
                <Typography sx={{ m: '0 1rem' }}>{count}</Typography>
                <IconButton
                  onClick={() => setCount(count + 1)}
                  disabled={count === remainingOrdersCount || disableActions}
                >
                  <PlusIcon />
                </IconButton>
              </Box>
              <Box>
                <Button
                  variant='contained'
                  onClick={async () => await handleClickAdd()}
                  disabled={disableActions}
                  data-cy='nnr-add-button'
                >
                  {t('add', 'Add')}
                </Button>
              </Box>
            </>
          </Box>
        )}

        {doesNotEnoughContainersAvailable && (
          <Box mt={theme.customSpacing.xs}>
            <NoContainersAlert
              containersAvailable={nnrOrder.suitableContainersCount}
              containersRequired={count}
            />
          </Box>
        )}
      </Box>
    )
  },
)
